import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Cpu,
  HardDrive,
  Network,
  Activity,
  Globe,
  Lightbulb,
  MemoryStick,
  Server,
} from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiStrip } from '@/components/common/KpiStrip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge, ServiceStatusBadge } from '@/components/common/StatusBadge';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { ProfileBadge } from '@/components/common/ProfileBadge';
import { StateChangesTable } from '@/components/dashboard/StateChangesTable';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { useNode, useNodeHistory } from '@/hooks/useNodes';
import { useNodeContext } from '@/hooks/useNodeContext';
import { formatDateTime, formatUptime, formatTimeAgo } from '@/utils/formatters';
import { parseHardware } from '@/utils/calculations';
import { cn } from '@/lib/utils';
import type { Period } from '@/types/metrics';

const portfolioSourceLabels: Record<string, string> = {
  label: 'Metadata Label',
  connectivity: 'CC Connectivity',
  deployment: 'Deployment Label',
  name_prefix: 'Name Prefix',
  gps: 'GPS Location',
  location: 'Location Info',
  profile: 'Profile Type',
};

export function NodeDetail() {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState<Period>('24h');
  const { node, loading: nodeLoading } = useNode(id!);
  const { events, loading: historyLoading } = useNodeHistory(id!, period);
  const { context, fetchContext } = useNodeContext();

  // Fetch context for problem nodes
  useEffect(() => {
    if (
      node &&
      (node.currentNodeState === 'UNREACHABLE' || node.connectedCloudConnectors?.length)
    ) {
      fetchContext(node.id);
    }
  }, [node, fetchContext]);

  if (nodeLoading) {
    return <PageLoader />;
  }

  if (!node) {
    return (
      <div className="dls-text-center dls-py-12">
        <h2 className="dls-text-2xl dls-font-bold dls-mb-2">Node not found</h2>
        <Link to="/nodes">
          <Button>Back to Nodes</Button>
        </Link>
      </div>
    );
  }

  const location = [node.street, node.city, node.state, node.country].filter(Boolean).join(', ');

  return (
    <div className="dls-space-y-6">
      <div className="dls-flex dls-items-center dls-gap-4">
        <Link to="/nodes">
          <Button variant="ghost" size="icon" aria-label="Back to all nodes">
            <ArrowLeft className="dls-h-5 dls-w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="dls-page-title">{node.name}</h1>
          <div className="dls-flex dls-items-center dls-gap-3 dls-mt-1 dls-flex-wrap">
            <StatusBadge state={node.currentNodeState} />
            <ProfileBadge profileName={node.profileName} />
            {node.tier && (
              <Badge variant="outline" className="dls-text-xs">
                Tier: {node.tier}
              </Badge>
            )}
            {node.portfolio && (
              <span className="dls-text-sm dls-text-muted-foreground">{node.portfolio}</span>
            )}
            {node.portfolioSource && (
              <Badge variant="secondary" className="dls-text-xs">
                via {portfolioSourceLabels[node.portfolioSource] || node.portfolioSource}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Problem context - suggested actions */}
      {context?.suggestedActions &&
        context.suggestedActions.length > 0 &&
        context.suggestedActions[0] !== 'No issues detected. Node is operating normally.' && (
          <Card className="dls-border-status-warning-tertiary dls-bg-status-warning-quarternary/50">
            <CardHeader className="dls-pb-2">
              <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2 dls-text-status-warning">
                <Lightbulb className="dls-h-4 dls-w-4" />
                Suggested Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="dls-space-y-1.5">
                {context.suggestedActions.map((action, i) => (
                  <li key={i} className="dls-text-sm dls-text-status-warning dls-flex dls-gap-2">
                    <span className="dls-text-status-warning dls-mt-0.5">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

      {/* Info strip */}
      <KpiStrip
        items={[
          { label: 'Uptime Since Reboot', value: formatUptime(node.bootTime) },
          { label: 'In Current State', value: node.nodeStateChangedAt ? formatTimeAgo(node.nodeStateChangedAt) : 'N/A' },
          { label: 'Software Version', value: node.softwareVersion || 'N/A' },
          { label: 'Last Contact', value: formatTimeAgo(node.lastContactedAt) },
          ...(node.clusterInfo ? [{
            label: 'Cluster',
            value: node.clusterInfo.clusterName,
            subtitle: `${node.clusterInfo.role ?? 'Member'} · ${node.clusterInfo.memberCount} member${node.clusterInfo.memberCount !== 1 ? 's' : ''}`,
          }] : []),
        ]}
      />

      {/* Resource Gauges */}
      <div className="dls-grid dls-gap-4 md:dls-grid-cols-3">
        <Card>
          <CardHeader className="dls-pb-2">
            <CardTitle className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-2 dls-whitespace-nowrap">
              <Cpu className="dls-h-4 dls-w-4" />
              CPU Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {node.cpuUsagePercent != null ? (
              <>
                <span className={cn(
                  'dls-text-2xl dls-font-bold',
                  node.cpuUsagePercent >= 90 ? 'dls-text-status-offline' :
                  node.cpuUsagePercent >= 70 ? 'dls-text-status-warning' : 'dls-text-status-online'
                )}>
                  {node.cpuUsagePercent.toFixed(1)}%
                </span>
                {node.cpuCores != null && (
                  <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">{node.cpuCores} cores</p>
                )}
                <div className="dls-w-full dls-bg-muted dls-rounded-full dls-h-2 dls-mt-2">
                  <div
                    className={cn(
                      'dls-h-2 dls-rounded-full dls-transition-all',
                      node.cpuUsagePercent >= 90 ? 'dls-bg-status-offline' :
                      node.cpuUsagePercent >= 70 ? 'dls-bg-status-warning' : 'dls-bg-status-online'
                    )}
                    style={{ width: `${Math.min(node.cpuUsagePercent, 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <span className="dls-text-2xl dls-font-bold dls-text-muted-foreground">N/A</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="dls-pb-2">
            <CardTitle className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-2 dls-whitespace-nowrap">
              <MemoryStick className="dls-h-4 dls-w-4" />
              Memory Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {node.memoryUsagePercent != null ? (
              <>
                <span className={cn(
                  'dls-text-2xl dls-font-bold',
                  node.memoryUsagePercent >= 90 ? 'dls-text-status-offline' :
                  node.memoryUsagePercent >= 70 ? 'dls-text-status-warning' : 'dls-text-status-online'
                )}>
                  {node.memoryUsagePercent.toFixed(1)}%
                </span>
                {node.memoryTotalBytes && (
                  <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">
                    {(Number(node.memoryTotalBytes) / (1024 ** 3)).toFixed(1)} GB total
                  </p>
                )}
                <div className="dls-w-full dls-bg-muted dls-rounded-full dls-h-2 dls-mt-2">
                  <div
                    className={cn(
                      'dls-h-2 dls-rounded-full dls-transition-all',
                      node.memoryUsagePercent >= 90 ? 'dls-bg-status-offline' :
                      node.memoryUsagePercent >= 70 ? 'dls-bg-status-warning' : 'dls-bg-status-online'
                    )}
                    style={{ width: `${Math.min(node.memoryUsagePercent, 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <span className="dls-text-2xl dls-font-bold dls-text-muted-foreground">N/A</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="dls-pb-2">
            <CardTitle className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-2 dls-whitespace-nowrap">
              <HardDrive className="dls-h-4 dls-w-4" />
              Disk Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {node.diskUsagePercent != null ? (
              <>
                <span className={cn(
                  'dls-text-2xl dls-font-bold',
                  node.diskUsagePercent >= 90 ? 'dls-text-status-offline' :
                  node.diskUsagePercent >= 70 ? 'dls-text-status-warning' : 'dls-text-status-online'
                )}>
                  {node.diskUsagePercent.toFixed(1)}%
                </span>
                {node.diskTotalBytes && (
                  <p className="dls-text-xs dls-text-muted-foreground dls-mt-1">
                    {(Number(node.diskTotalBytes) / (1024 ** 3)).toFixed(1)} GB total
                  </p>
                )}
                <div className="dls-w-full dls-bg-muted dls-rounded-full dls-h-2 dls-mt-2">
                  <div
                    className={cn(
                      'dls-h-2 dls-rounded-full dls-transition-all',
                      node.diskUsagePercent >= 90 ? 'dls-bg-status-offline' :
                      node.diskUsagePercent >= 70 ? 'dls-bg-status-warning' : 'dls-bg-status-online'
                    )}
                    style={{ width: `${Math.min(node.diskUsagePercent, 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <span className="dls-text-2xl dls-font-bold dls-text-muted-foreground">N/A</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Device Information</CardTitle>
          </CardHeader>
          <CardContent className="dls-space-y-4">
            <div className="dls-grid dls-grid-cols-2 dls-gap-4">
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Serial Number</p>
                <p className="dls-font-mono">{node.hardwareSerialNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Vendor</p>
                <p>{parseHardware(node).vendor}</p>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Model</p>
                <p>{parseHardware(node).model || 'N/A'}</p>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Profile</p>
                <ProfileBadge profileName={node.profileName} />
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Deployment</p>
                <p>{node.deployment || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-2">
              <MapPin className="dls-h-5 dls-w-5" />
              Location
            </CardTitle>
          </CardHeader>
          <CardContent className="dls-space-y-4">
            <div className="dls-grid dls-grid-cols-2 dls-gap-4">
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Portfolio</p>
                <div className="dls-flex dls-items-center dls-gap-2">
                  <p>{node.portfolio || 'N/A'}</p>
                  {node.portfolioSource && (
                    <Badge variant="outline" className="dls-text-xs">
                      {portfolioSourceLabels[node.portfolioSource] || node.portfolioSource}
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Business Unit</p>
                <p>{node.bu || 'N/A'}</p>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Group</p>
                <p>{node.group || 'N/A'}</p>
              </div>
              <div>
                <p className="dls-text-sm dls-text-muted-foreground">Address</p>
                <p>{location || 'N/A'}</p>
              </div>
              {node.latitude != null && node.longitude != null && (
                <div className="dls-col-span-2">
                  <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">
                    <Globe className="dls-h-3 dls-w-3" />
                    GPS Coordinates
                  </p>
                  <p className="dls-font-mono dls-text-sm">
                    {node.latitude.toFixed(6)}, {node.longitude.toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cloud Connectors + Services */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {/* Cloud Connectors */}
        {node.connectedCloudConnectors && node.connectedCloudConnectors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-2">
                <Network className="dls-h-5 dls-w-5" />
                Cloud Connectors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-space-y-2 dls-max-h-[300px] dls-overflow-y-auto">
                {context?.cloudConnectors
                  ? context.cloudConnectors.map((cc) => (
                      <div
                        key={cc.name}
                        className="dls-flex dls-items-center dls-justify-between dls-p-2 dls-rounded dls-bg-muted"
                      >
                        <span className="dls-font-medium">{cc.name}</span>
                        <StatusBadge state={cc.status} />
                      </div>
                    ))
                  : node.connectedCloudConnectors.map((ccName) => (
                      <div
                        key={ccName}
                        className="dls-flex dls-items-center dls-justify-between dls-p-2 dls-rounded dls-bg-muted"
                      >
                        <span className="dls-font-medium">{ccName}</span>
                      </div>
                    ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connected Edge Nodes (CC reciprocity — shown for Cloud Connector nodes) */}
        {node.connectedEdgeNodes && node.connectedEdgeNodes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-2">
                <Server className="dls-h-5 dls-w-5" />
                Connected Edge Nodes ({node.connectedEdgeNodes.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-space-y-2 dls-max-h-[300px] dls-overflow-y-auto">
                {node.connectedEdgeNodes.map((edge) => (
                  <Link
                    key={edge.id}
                    to={`/nodes/${edge.id}`}
                    className="dls-flex dls-items-center dls-justify-between dls-p-2 dls-rounded dls-bg-muted hover:dls-bg-muted/80 dls-transition-colors"
                  >
                    <span className="dls-font-medium">{edge.name}</span>
                    <StatusBadge state={edge.currentNodeState} />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services */}
        {context?.services && context.services.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="dls-flex dls-items-center dls-gap-2">
                <Activity className="dls-h-5 dls-w-5" />
                Services ({context.services.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-space-y-2 dls-max-h-[300px] dls-overflow-y-auto">
                {context.services.map((svc) => (
                  <div
                    key={svc.id}
                    className="dls-flex dls-items-center dls-justify-between dls-p-2 dls-rounded dls-bg-muted"
                  >
                    <span className="dls-font-medium dls-truncate">{svc.name}</span>
                    <div className="dls-flex dls-items-center dls-gap-2">
                      <span className="dls-text-xs dls-text-muted-foreground">
                        {svc.runningContainers ?? '—'}
                      </span>
                      <ServiceStatusBadge status={svc.healthStatus} className="dls-text-xs" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Timestamps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="dls-grid dls-gap-4 sm:dls-grid-cols-2 lg:dls-grid-cols-3">
            <div>
              <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">Provisioned <InfoTooltip text="When this node was created in the Secure Edge portal — the setup/configuration step before physical deployment." /></p>
              <p>{formatDateTime(node.neeveCreatedAt)}</p>
            </div>
            <div>
              <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">Commissioned <InfoTooltip text="When this node first phoned home — the physical install is complete and the node is in service at the site." /></p>
              <p>{formatDateTime(node.firstContactedAt)}</p>
            </div>
            <div>
              <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">Last Contact <InfoTooltip text="The most recent heartbeat received from this node." /></p>
              <p>{formatDateTime(node.lastContactedAt)}</p>
            </div>
            <div>
              <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">Last State Change <InfoTooltip text="When this node last transitioned between online and offline states." /></p>
              <p>{formatDateTime(node.nodeStateChangedAt)}</p>
            </div>
            <div>
              <p className="dls-text-sm dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">Last Boot <InfoTooltip text="When this node was last rebooted or powered on." /></p>
              <p>{formatDateTime(node.bootTime)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification timeline for problem context */}
      {context?.notifications && context.notifications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Platform Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="dls-space-y-3 dls-max-h-[300px] dls-overflow-y-auto">
              {context.notifications.map((notif) => (
                <div key={notif.id} className="dls-border-l-2 dls-border-muted dls-pl-3 dls-pb-1">
                  <div className="dls-flex dls-items-center dls-gap-2">
                    <span className="dls-text-xs dls-text-muted-foreground">
                      {formatTimeAgo(notif.occurredAt)}
                    </span>
                    {notif.displayType && (
                      <Badge variant="outline" className="dls-text-xs">
                        {notif.displayType}
                      </Badge>
                    )}
                  </div>
                  <p className="dls-text-sm dls-mt-0.5">{notif.message || notif.type}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* State history */}
      <div>
        <div className="dls-flex dls-items-center dls-justify-between dls-mb-4">
          <h2 className="dls-text-xl dls-font-semibold">State History</h2>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList>
              <TabsTrigger value="24h">24h</TabsTrigger>
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="30d">30d</TabsTrigger>
              <TabsTrigger value="90d">90d</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {historyLoading ? <PageLoader /> : <StateChangesTable events={events} />}
      </div>
    </div>
  );
}
